import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, ICON_SIZE } from '../chrome/icons';
import type { CanvasHandle, ScreenBox } from '../useEditorCanvas';

/**
 * The full view Play opens in a tab of its own, as Figma's does: the file as it stands, read
 * only, one artboard at a time on black. The arrows and Space go through the boards, Escape
 * closes the tab, and a board that animates plays when it arrives. Nothing else reaches the
 * canvas: no pan, zoom, picking or shortcuts, the view stays on the board.
 */

/** The artboard the tab was opened on, when this tab is a full view. */
export function presentTarget(): string | null {
  return new URLSearchParams(window.location.search).get('present');
}

export function presentUrl(documentId: string, board: string | null): string {
  return `/f/${documentId}?present=${encodeURIComponent(board ?? '')}`;
}

export function PresentStage({ canvas, ready, onShow }: { canvas: Pick<CanvasHandle, 'artboards' | 'showArtboard' | 'boxOf'>; ready: boolean; onShow: (id: string) => void }) {
  const [boards, setBoards] = useState<readonly string[]>([]);
  const [at, setAt] = useState(0);
  const [box, setBox] = useState<ScreenBox | null>(null);

  // The scene arrives a little after the file opens.
  useEffect(() => {
    if (!ready) return;
    const read = () => {
      const found = canvas.artboards().map(board => board.id);
      if (!found.length) return false;
      setBoards(found);
      setAt(Math.max(0, found.indexOf(presentTarget() ?? '')));
      return true;
    };
    if (read()) return;
    const timer = window.setInterval(() => { if (read()) window.clearInterval(timer); }, 200);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const id = boards[at];
  const fit = useCallback(() => {
    if (!id) return;
    canvas.showArtboard(id);
    requestAnimationFrame(() => setBox(canvas.boxOf(id)));
  }, [canvas, id]);

  useEffect(() => {
    if (!id) return;
    fit();
    onShow(id);
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fit]);

  const go = useCallback((by: number) => setAt(was => Math.max(0, Math.min(boards.length - 1, was + by))), [boards.length]);

  // In the capture phase and registered once, so it is heard before the canvas and the page: the
  // board keys page, Escape closes, and every other key stops here (the browser's own still work).
  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const by = event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ' ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'PageUp' ? -1 : 0;
      event.stopPropagation();
      if (event.key === 'Escape') window.close();
      else if (!by) return;
      event.preventDefault();
      goRef.current(by);
    };
    const quiet = (event: KeyboardEvent) => event.stopPropagation();
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', quiet, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', quiet, true);
    };
  }, []);

  // A cover over the whole canvas takes the pointer, the wheel and pinches, so the board can't be
  // moved, zoomed or picked. Wheel and gestures need a listener that may cancel them.
  const shield = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = shield.current;
    if (!element) return;
    const stop = (event: Event) => event.preventDefault();
    element.addEventListener('wheel', stop, { passive: false });
    element.addEventListener('gesturestart', stop);
    element.addEventListener('gesturechange', stop);
    return () => {
      element.removeEventListener('wheel', stop);
      element.removeEventListener('gesturestart', stop);
      element.removeEventListener('gesturechange', stop);
    };
  }, []);

  return <>
    <div ref={shield} aria-hidden className="present-shield" onContextMenu={event => event.preventDefault()} />
    {box ? <div aria-hidden className="present-mask" style={{ left: box.left, top: box.top, width: box.right - box.left, height: box.bottom - box.top }} /> : null}
    {boards.length > 1 ? <div className="present-pager">
      <button type="button" aria-label="Previous artboard" disabled={at === 0} onClick={() => go(-1)}><Icon name="chevron-right" size={ICON_SIZE.small} /></button>
      <span className="tnum">{at + 1} / {boards.length}</span>
      <button type="button" aria-label="Next artboard" disabled={at === boards.length - 1} onClick={() => go(1)}><Icon name="chevron-right" size={ICON_SIZE.small} /></button>
    </div> : null}
  </>;
}
